import React from 'react';
import classNames from 'classnames';
import styles from '../assets/grid.module.scss';
import { Paginated } from 'src/services/api-handlers/pagination';
import Grid, { Column } from 'src/components/react-table/Grid';
import { QueryParams } from 'src/types/grid';
import { CellProps } from 'react-table';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import { INonBilledSession } from 'src/store/reducers/nonBilledSessionsReport-reducer';
import user from 'src/services/user-auth';
import UserRole from 'src/enumerables/user-role';
import CaseEnum from 'src/enumerables/case-enum';
import OrderEnum from 'src/enumerables/order-enum';
import { getValFromEnum } from 'src/components/redux-form/utils/dropdown-values';
import { changeToZeroMoment } from 'src/helpers/timeHelpers';
import { enumLabel, studentName } from 'src/helpers/nonBilledSessionsReport/groupCsv';

interface Props {
    data: Paginated<INonBilledSession>;
    getData: (params: QueryParams) => void;
}

type Cell = CellProps<INonBilledSession>;

const plain = { disableSortBy: true, disableFilters: true };

const buildColumns = (): Column<INonBilledSession>[] => {
    const caseTypes = getValFromEnum(CaseEnum ? CaseEnum.getEnum('caseType') : {});
    const orderTypes = getValFromEnum(OrderEnum ? OrderEnum.getEnum('OrderType') : {});
    const isAdmin = user.getRoles().includes(UserRole.ROLE_ADMIN);

    return [
        { Header: 'ID', accessor: 'id', ...plain },
        {
            Header: 'Date',
            accessor: 'seanceDate',
            ...plain,
            Cell: ({ row: { original } }: Cell) =>
                original.seanceDate ? changeToZeroMoment(original.seanceDate, 'MM/DD/YYYY') : '',
        },
        {
            Header: 'Time',
            accessor: 'startTime',
            ...plain,
            Cell: ({ row: { original } }: Cell) =>
                [original.startTime, original.endTime]
                    .map((t) => (t ? changeToZeroMoment(t, 'hh:mm A') : ''))
                    .join(' - '),
        },
        {
            Header: 'Provider',
            id: 'provider',
            ...plain,
            Cell: ({ row: { original } }: Cell) =>
                isAdmin ? (
                    <Link
                        to={Router.generate(routes.PROVIDER_DETAIL, {
                            id: original.provider?.id,
                            tab: 'main-info',
                            subId: '',
                        })}
                    >
                        {original.provider?.fullName}
                    </Link>
                ) : (
                    original.provider?.fullName ?? ''
                ),
        },
        {
            Header: 'Student',
            id: 'student',
            ...plain,
            Cell: ({ row: { original } }: Cell) => studentName(original),
        },
        {
            Header: 'Service Type',
            id: 'serviceType',
            ...plain,
            Cell: ({ row: { original } }: Cell) => original.serviceType?.title ?? '',
        },
        {
            Header: 'Case Number',
            id: 'caseNumber',
            ...plain,
            Cell: ({ row: { original } }: Cell) => original.occasion?.caseNumber ?? '',
        },
        {
            Header: 'Case Type',
            id: 'caseType',
            ...plain,
            Cell: ({ row: { original } }: Cell) => enumLabel(caseTypes, original.occasion?.caseType),
        },
        {
            Header: 'Order Type',
            id: 'orderType',
            ...plain,
            Cell: ({ row: { original } }: Cell) => enumLabel(orderTypes, original.occasion?.orderType),
        },
    ];
};

const NonBilledSessionsReportGrid: React.FunctionComponent<Props> = ({ data, getData }: Props) => {
    const columns = React.useMemo(buildColumns, []);

    return (
        <div className={classNames(styles.grid)}>
            <Grid<INonBilledSession> columns={columns} data={data} getData={getData} />
        </div>
    );
};

export default NonBilledSessionsReportGrid;
